import { FC, useState, MouseEvent, useEffect } from 'react';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import ConfirmationDialogV2 from 'src/components/DialogBox/ConfirmationDialogV2/ConfirmationDialogV2';
import RejectDialogV2 from 'src/components/DialogBox/RejectDialogV2/RejectDialogV2';
import ViewVoucherPaymentDialog from 'src/components/DialogBox/VoucherPaymentDialog/ViewVoucherPaymentDialog';
import AddVoucherPaymentDialog from 'src/components/DialogBox/VoucherPaymentDialog/AddVoucherPaymentDialog';
import {
  apiUrls,
  successMessages,
  voucherPaymentDialogTitles,
  voucherPaymentDialogSubtitles,
  DATE_FORMATS,
  userRoles,
  dataStorageKeywords,
  voucherPaymentActionButtons,
} from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  VoucherPayment,
  VoucherPaymentStatus,
  VoucherPaymentActionMenuProps,
  VoucherPaymentPayload,
} from 'src/models/VoucherPayment.interface';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { StyledDatePickerErrorText } from 'src/components/StyledComponents/StyledUnitPicker';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { voucherPaymentInitialValues } from 'src/utils/initialValues.utils';
import { voucherPaymentDynamicValidation } from '../../utils/validationScheme.utils';
import TextField from 'src/components/InputFields/TextField';
import PaymentModePicker from 'src/components/InputFields/PaymentModePicker';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import FormikSelectField from 'src/components/InputFields/FormikSelectField';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import {
  VoucherPaymentFieldRow,
  VoucherPaymentFieldItem,
  VoucherPaymentFieldItemEnd,
  VoucherPaymentChequeRow,
  VoucherPaymentChequeDateContainer,
  VoucherPaymentChequeDateLabel,
  VoucherPaymentBankRow,
  VoucherPaymentOnlineRow,
} from 'src/components/DialogBox/VoucherPaymentDialog/VoucherPaymentDialog.styles';
import {
  formatNumberWithCommas,
  handleNumberFieldChange,
} from 'src/utils/format.utils';

const VoucherPaymentActionMenu: FC<VoucherPaymentActionMenuProps> = ({
  id,
  voucherId,
  status,
  banks,
  fetchPayments,
  deletePayment,
  onBanksFetch,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewData, setViewData] = useState<VoucherPayment | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchUserRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setUserRole(role);
    };
    fetchUserRole();
  }, []);

  const isAdminOrOwner =
    userRole === userRoles.ADMIN || userRole === userRoles.OWNER;

  const isPendingApproval =
    status === VoucherPaymentStatus.PENDING_APPROVAL ||
    status === 'Pending Approval';

  const canView = true;
  const canEdit = isPendingApproval;
  const canApproveReject = isPendingApproval && isAdminOrOwner;
  const canDelete = isPendingApproval;

  const accountOptions = banks.map((bank) => ({
    label: `${bank.bankName} - ${bank.accountNumber}`,
    value: bank.id.toString(),
  }));

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const formik = useFormik({
    initialValues: voucherPaymentInitialValues,
    validationSchema: voucherPaymentDynamicValidation,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Form submission handled by individual action handlers
    },
  });

  useEffect(() => {
    const mode = formik.values.paymentMode;
    if (
      (mode === PaymentMode.CHEQUE || mode === PaymentMode.ONLINE) &&
      banks.length === 0 &&
      onBanksFetch
    ) {
      onBanksFetch();
    }
  }, [formik.values.paymentMode, banks.length, onBanksFetch]);

  const handleViewDialogOpen = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_VOUCHER_PAYMENT_BY_ID,
        { params: { id } }
      );
      const paymentData = response.data;

      setViewData({
        id: paymentData.id,
        voucherId: paymentData.voucherId,
        paymentDate: paymentData.paymentDate,
        paidAmount: paymentData.paidAmount,
        paymentMode: paymentData.paymentMode,
        receiverName: paymentData.receiverName,
        status: paymentData.status,
        approvedBy: paymentData.approvedBy,
        approvedDate: paymentData.approvedDate,
        rejectionNotes: paymentData.rejectionNotes,
        chequeNumber: paymentData.chequeNumber,
        chequeDate: paymentData.chequeDate,
        chequeStatus: paymentData.chequeStatus,
        chequeRealizedDate: paymentData.chequeRealizedDate,
        bankName: paymentData.bankName,
        accountNumber: paymentData.accountNumber,
        bankAccountName: paymentData.bankAccountName,
        paymentRefNo: paymentData.paymentRefNo,
        createdAt: paymentData.createdAt,
        updatedAt: paymentData.updatedAt,
      });
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setViewData(null);
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    if (deleteLoading) return;
    setDeleteLoading(true);
    try {
      if (id) {
        await deletePayment(id);
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleApproveDialogOpen = () => {
    setApproveDialogOpen(true);
    handleClose();
  };

  const handleApproveConfirm = async () => {
    if (approveLoading) return;
    setApproveLoading(true);
    try {
      const response = await axiosTokenInstance.patch(
        apiUrls.TOGGLE_VOUCHER_PAYMENT_STATUS,
        {
          id: id,
          status: 'Approved',
        }
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_APPROVED_SUCCESSFULLY,
        'success'
      );
      fetchPayments();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setApproveLoading(false);
      setApproveDialogOpen(false);
    }
  };

  const handleRejectDialogOpen = () => {
    setRejectDialogOpen(true);
    handleClose();
  };

  const handleRejectConfirm = async (reason: string) => {
    if (rejectLoading) return;
    setRejectLoading(true);
    try {
      const response = await axiosTokenInstance.patch(
        apiUrls.TOGGLE_VOUCHER_PAYMENT_STATUS,
        {
          id: id,
          status: 'Rejected',
          rejectionNotes: reason,
        }
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_REJECTED_SUCCESSFULLY,
        'success'
      );
      fetchPayments();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setRejectLoading(false);
      setRejectDialogOpen(false);
    }
  };

  const handleEditDialogOpen = async () => {
    handleClose();
    try {
      const [paymentResponse, voucherResponse] = await Promise.all([
        axiosTokenInstance.get(apiUrls.GET_VOUCHER_PAYMENT_BY_ID, {
          params: { id },
        }),
        axiosTokenInstance.get(apiUrls.GET_VOUCHER_BY_ID, {
          params: { id: voucherId },
        }),
      ]);
      const paymentData = paymentResponse.data;
      const voucherData = voucherResponse.data;

      const voucherBillAmount = Number(voucherData?.billAmount) || 0;
      const voucherTotalPaid = Number(voucherData?.totalPaidAmount) || 0;
      const currentPaymentAmount = Number(paymentData.paidAmount) || 0;
      setRemainingAmount(
        Math.max(voucherBillAmount - voucherTotalPaid + currentPaymentAmount, 0)
      );

      formik.setValues({
        id: paymentData.id,
        voucherId: paymentData.voucherId,
        receiverName: paymentData.receiverName || '',
        paymentDate:
          paymentData.paymentDate?.split('T')[0] ||
          new Date().toISOString().split('T')[0],
        amount: paymentData.paidAmount?.toString() || '',
        paymentMode: paymentData.paymentMode,
        chequeNumber: paymentData.chequeNumber || '',
        chequeDate:
          paymentData.chequeDate?.split('T')[0] ||
          new Date().toISOString().split('T')[0],
        bankAccountId:
          paymentData.bankAccountId?.toString() ||
          paymentData.bankAccount?.id?.toString() ||
          '',
        transactionReference: paymentData.paymentRefNo || '',
      });

      // Fetch banks if needed based on payment mode
      if (
        (paymentData.paymentMode === PaymentMode.CHEQUE ||
          paymentData.paymentMode === PaymentMode.ONLINE) &&
        banks.length === 0 &&
        onBanksFetch
      ) {
        onBanksFetch();
      }

      setEditDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    formik.resetForm();
  };

  const handleEditSubmit = async () => {
    if (loading) return;

    const touchedFields: Record<string, boolean> = {
      receiverName: true,
      paymentDate: true,
      amount: true,
      paymentMode: true,
    };

    if (formik.values.paymentMode === PaymentMode.CHEQUE) {
      touchedFields.chequeNumber = true;
      touchedFields.chequeDate = true;
      touchedFields.bankAccountId = true;
    } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
      touchedFields.bankAccountId = true;
      touchedFields.transactionReference = true;
    }

    try {
      await voucherPaymentDynamicValidation.validate(formik.values, {
        abortEarly: false,
      });
    } catch (validationError) {
      const yupError = validationError as {
        inner: Array<{ path: string; message: string }>;
      };
      const errors = yupError.inner.reduce(
        (
          acc: Record<string, string>,
          error: { path: string; message: string }
        ) => {
          acc[error.path] = error.message;
          return acc;
        },
        {}
      );
      await formik.setTouched(touchedFields, false);
      formik.setErrors(errors);
      return;
    }

    const enteredAmount = parseFloat(formik.values.amount.replace(/,/g, ''));
    if (remainingAmount > 0 && enteredAmount > remainingAmount) {
      await formik.setTouched({ ...touchedFields }, false);
      formik.setErrors({
        amount: `Amount exceeds remaining payable amount (${remainingAmount.toLocaleString()})`,
      });
      return;
    }

    setLoading(true);

    try {
      const cleanAmount = formik.values.amount.replace(/,/g, '');
      const payload: VoucherPaymentPayload = {
        id: id,
        receiverName: formik.values.receiverName,
        paymentDate: dayjs(formik.values.paymentDate).format(DATE_FORMATS.API),
        paidAmount: parseFloat(cleanAmount),
        paymentMode: formik.values.paymentMode,
      };

      if (formik.values.paymentMode === PaymentMode.CHEQUE) {
        payload.chequeNumber = formik.values.chequeNumber;
        payload.chequeDate = dayjs(formik.values.chequeDate).format(
          DATE_FORMATS.API
        );
        payload.bankAccountId = formik.values.bankAccountId;
      } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
        payload.bankAccountId = formik.values.bankAccountId;
        payload.paymentRefNo = formik.values.transactionReference;
      }

      const response = await axiosTokenInstance.put(
        apiUrls.EDIT_VOUCHER_PAYMENT,
        payload,
        { params: { id } }
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_UPDATED_SUCCESSFULLY,
        'success'
      );
      fetchPayments();
      handleEditDialogClose();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          {canView && (
            <MenuItemWithIcon
              onClick={handleViewDialogOpen}
              iconClassName="bx bx-file-find"
              text={voucherPaymentActionButtons.VIEW}
            />
          )}
          {canEdit && (
            <MenuItemWithIcon
              onClick={handleEditDialogOpen}
              iconClassName="bx bx-edit"
              text={voucherPaymentActionButtons.EDIT}
            />
          )}
          {canApproveReject && (
            <>
              <MenuItemWithIcon
                onClick={handleApproveDialogOpen}
                iconClassName="bx bx-check"
                text={voucherPaymentActionButtons.APPROVE}
              />
              <MenuItemWithIcon
                onClick={handleRejectDialogOpen}
                iconClassName="bx bx-x"
                text={voucherPaymentActionButtons.REJECT}
              />
            </>
          )}
          {canDelete && (
            <MenuItemWithIcon
              onClick={handleDeleteDialogOpen}
              iconClassName="bx bx-trash"
              text={voucherPaymentActionButtons.DELETE}
            />
          )}
        </MenuItemsWrapper>
      </ActionMenuPopover>
      <ViewVoucherPaymentDialog
        open={viewDialogOpen}
        onClose={handleViewDialogClose}
        payment={viewData}
      />
      <ConfirmationDialogV2
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={voucherPaymentDialogTitles.DELETE}
        subtitle={voucherPaymentDialogSubtitles.DELETE}
        subtitleSecondary={voucherPaymentDialogSubtitles.DELETE_SECONDARY}
        confirmText={voucherPaymentActionButtons.DELETE}
        confirmButtonColor="var(--error)"
        iconColor="var(--error)"
        loading={deleteLoading}
      />
      <ConfirmationDialogV2
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={voucherPaymentDialogTitles.APPROVE}
        subtitle={voucherPaymentDialogSubtitles.APPROVE}
        subtitleSecondary={voucherPaymentDialogSubtitles.APPROVE_SECONDARY}
        confirmText={voucherPaymentActionButtons.APPROVE}
        confirmButtonColor="var(--success)"
        iconColor="var(--warning)"
        loading={approveLoading}
      />
      <RejectDialogV2
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
        title={voucherPaymentDialogTitles.REJECT}
        subtitle={voucherPaymentDialogSubtitles.REJECT}
        subtitleSecondary={voucherPaymentDialogSubtitles.REJECT_SECONDARY}
        placeholder="Reason for rejection"
        confirmText={voucherPaymentActionButtons.REJECT}
        loading={rejectLoading}
      />
      <AddVoucherPaymentDialog
        open={editDialogOpen}
        handleClose={handleEditDialogClose}
        title={voucherPaymentDialogTitles.EDIT}
        subtitle={voucherPaymentDialogSubtitles.EDIT}
        onSubmit={formik.handleSubmit}
        buttonDisabled={loading}
        buttonLabel={voucherPaymentActionButtons.UPDATE}
        onSaveClick={handleEditSubmit}
        paymentModeSection={
          <>
            <PaymentModePicker
              color="var(--subtext)"
              width="48.5%"
              id="paymentMode"
              label="Payment Method"
              formikProps={formik}
              customMessage="Select payment method"
              showLabel={false}
            />
            {formik.values.paymentMode === PaymentMode.CHEQUE ? (
              <>
                <VoucherPaymentChequeRow>
                  <VoucherPaymentFieldItem>
                    <TextField
                      color="var(--subtext)"
                      width="100%"
                      type="number"
                      id="chequeNumber"
                      label="Cheque Number"
                      formikProps={formik}
                    />
                  </VoucherPaymentFieldItem>
                  <VoucherPaymentFieldItem>
                    <VoucherPaymentChequeDateContainer>
                      <VoucherPaymentChequeDateLabel>
                        Cheque Date
                      </VoucherPaymentChequeDateLabel>
                      <CreatedDatePicker
                        fontSize="14px"
                        height="40px"
                        color="var(--subtext)"
                        width="100%"
                        name="chequeDate"
                        placeholder="Select date"
                        formikProps={formik}
                        showLabel={false}
                      />
                      {formik.touched.chequeDate &&
                        formik.errors.chequeDate && (
                          <StyledDatePickerErrorText>
                            <ErrorIcon />
                            {formik.errors.chequeDate}
                          </StyledDatePickerErrorText>
                        )}
                    </VoucherPaymentChequeDateContainer>
                  </VoucherPaymentFieldItem>
                </VoucherPaymentChequeRow>
                <VoucherPaymentBankRow>
                  <VoucherPaymentFieldItem>
                    <FormikSelectField
                      id="bankAccountId"
                      label="Bank Account"
                      formikProps={formik}
                      width="48.5%"
                      options={accountOptions}
                    />
                  </VoucherPaymentFieldItem>
                </VoucherPaymentBankRow>
              </>
            ) : formik.values.paymentMode === PaymentMode.ONLINE ? (
              <VoucherPaymentOnlineRow>
                <VoucherPaymentFieldItem>
                  <FormikSelectField
                    id="bankAccountId"
                    label="Bank Account"
                    formikProps={formik}
                    width="100%"
                    options={accountOptions}
                  />
                </VoucherPaymentFieldItem>
                <VoucherPaymentFieldItemEnd>
                  <TextField
                    color="var(--subtext)"
                    width="100%"
                    id="transactionReference"
                    label="Payment Reference"
                    formikProps={formik}
                  />
                </VoucherPaymentFieldItemEnd>
              </VoucherPaymentOnlineRow>
            ) : null}
          </>
        }
      >
        <VoucherPaymentFieldRow>
          <VoucherPaymentFieldItem>
            <TextField
              color="var(--subtext)"
              width="100%"
              id="receiverName"
              label="Received By"
              formikProps={formik}
            />
          </VoucherPaymentFieldItem>
          <VoucherPaymentFieldItemEnd>
            <CreatedDatePicker
              mb={3}
              fontSize="14px"
              label="Payment Date"
              showLabel={true}
              color="var(--subtext)"
              width="100%"
              height="40px"
              name="paymentDate"
              placeholder="Payment Date"
              formikProps={formik}
            />
            {formik.touched.paymentDate && formik.errors.paymentDate && (
              <StyledDatePickerErrorText top="2px">
                <ErrorIcon />
                {formik.errors.paymentDate}
              </StyledDatePickerErrorText>
            )}
          </VoucherPaymentFieldItemEnd>
        </VoucherPaymentFieldRow>
        <VoucherPaymentFieldItem>
          <TextField
            color="var(--subtext)"
            width="100%"
            id="amount"
            label="Amount"
            formikProps={formik}
            type="text"
            value={formatNumberWithCommas(formik.values.amount)}
            onChange={(e) => handleNumberFieldChange(e, formik, 'amount')}
            placeholder={
              remainingAmount > 0
                ? `Remaining: ${remainingAmount.toLocaleString()}`
                : ''
            }
          />
        </VoucherPaymentFieldItem>
      </AddVoucherPaymentDialog>
    </>
  );
};

export default VoucherPaymentActionMenu;
